// The room's sound sources (2026-10-29): one PannerNode per manifest audio source for the direction; the level and
// tone by where the listener stands are computed here per listener update (directivity.ts) and glide. The panners
// themselves are omnidirectional with no distance attenuation.
//
//   input -> shelf (near field) -> lowpass (off axis) -> per source: gain (1/sqrt(n)) -> att (cone x distance) -> panner -> out
//   input -> port lowpass -> fade -> att (level x distance) -> port panner -> out        (rooms with a port)
//
// A room without audio.directivity gets DEFAULT_DIRECTIVITY: the cone and distance law the panners had before, the
// shelf at 0 dB (an identity filter) and the low-pass at the Nyquist frequency (an identity filter).
//
// Room switches and teleports: a panner's direction follows the listener sample by sample, so a listener that jumps
// (or glides fast across the room) jumps every audible panner's pan, a click. Instead the speakers dip: their gains
// fall linearly to 0 over DIP_S, the listener jumps at that moment (engine.ts schedules it at `jumpPending`), and the
// gains rise again (new sources on a room switch). The reverb, which has no direction, keeps playing through it.
//
// Phones (`light`): no near-field shelf, and the port goes to `out` without a panner (bass, centred). Per source one
// GainNode more than before; per room one BiquadFilterNode, plus four nodes for a port (three on phones).
// No node class is used that an OfflineAudioContext lacks, so offline checks run this same code.

import type { Directivity } from '@lr3d/shared';
import { DEFAULT_DIRECTIVITY, centreOf, coneGain, distanceGain, nearShelfDb, offAxisDeg, offAxisLowpass } from './directivity';

type V3 = [number, number, number];
export interface SourceSpec { id: string; position: V3; forward: V3 }
interface Speaker { id: string; src: SourceSpec; panner: PannerNode; gain: GainNode; att: GainNode }
interface Port { lp: BiquadFilterNode; fade: GainNode; att: GainNode; panner: PannerNode | null; position: V3 }

/** Fall time (s) of the speakers' dip at a room switch or teleport (linear to 0). */
export const DIP_S = 0.04;
/** Rise after the dip: time constant (s); within 1 % after ~5 constants. */
const RISE_S = 0.03;
/** Time constant (s) of the per-listener-update changes (updates come at most every 33 ms). */
const FOLLOW_S = 0.02;
/** Length (s) of the low-pass's glide to the Nyquist frequency when a room has no off-axis low-pass. */
const OPEN_S = 0.08;
/** Butterworth: a BiquadFilterNode's lowpass Q is in dB (Web Audio spec), 20 log10(1/sqrt 2). */
const Q_BUTTERWORTH_DB = -3.0103;
/** Nodes of removed speakers are disconnected this long (ms) after their gain reached 0. */
const REMOVE_AFTER_MS = 300;

export class Speakers {
  private shelf: BiquadFilterNode | null;
  private lp: BiquadFilterNode;
  private list: Speaker[] = [];
  private port: Port | null = null;
  private dir: Directivity = DEFAULT_DIRECTIVITY;
  private listener: V3 = [0, 0, 0];
  private nyquist: number;
  private jumpAt = -Infinity;

  constructor(private ctx: BaseAudioContext, input: AudioNode, private out: AudioNode, private panning: PanningModelType,
    sources: SourceSpec[], private light = false) {
    this.nyquist = ctx.sampleRate / 2;
    this.lp = ctx.createBiquadFilter();
    this.lp.type = 'lowpass';
    this.lp.Q.value = Q_BUTTERWORTH_DB;
    this.lp.frequency.value = this.nyquist;
    if (light) { this.shelf = null; input.connect(this.lp); }
    else {
      this.shelf = ctx.createBiquadFilter();
      this.shelf.type = 'lowshelf';
      this.shelf.gain.value = 0;
      input.connect(this.shelf).connect(this.lp);
    }
    const g = 1 / Math.sqrt(sources.length);
    for (const s of sources) this.list.push(this.add(s, g));
    this.apply(true);
  }

  /** Source ids and gain targets (tests, HUD). */
  get info(): { id: string; gain: number }[] {
    const g = 1 / Math.sqrt(this.list.length);
    return this.list.map((s) => ({ id: s.id, gain: g }));
  }

  /** The port's position, null without one (tests, HUD). */
  get portInfo(): { position: V3 } | null {
    return this.port ? { position: this.port.position } : null;
  }

  /** The context time the listener should jump at (the bottom of a dip in progress), or null. */
  get jumpPending(): number | null {
    return this.jumpAt > this.ctx.currentTime ? this.jumpAt : null;
  }

  /**
   * Dip every speaker and the port for a listener jump (a teleport within a room); returns the time the listener
   * should jump at. A dip in progress is reused.
   */
  dip(): number {
    const pending = this.jumpPending;
    if (pending !== null) return pending;
    const t = this.ctx.currentTime;
    const at = (this.jumpAt = t + DIP_S);
    const g = 1 / Math.sqrt(this.list.length);
    for (const s of this.list) { fadeOut(s.gain.gain, t, at); fadeIn(s.gain.gain, g, at); }
    if (this.port) { fadeOut(this.port.fade.gain, t, at); fadeIn(this.port.fade.gain, 1, at); }
    return at;
  }

  private add(s: SourceSpec, gain: number): Speaker {
    const p = this.ctx.createPanner();
    p.panningModel = this.panning;
    p.distanceModel = 'inverse';
    p.rolloffFactor = 0; // distance: att
    p.coneInnerAngle = 360; // cone: att
    p.coneOuterAngle = 360;
    place(p, s.position, s.forward, this.ctx);
    const g = this.ctx.createGain();
    g.gain.value = gain;
    const att = this.ctx.createGain();
    att.gain.value = 0;
    this.lp.connect(g).connect(att).connect(p).connect(this.out);
    return { id: s.id, src: s, panner: p, gain: g, att };
  }

  /**
   * Another room's sources (room switch; the music keeps playing): the old speakers fall to 0 (the dip), the listener
   * jumps, and a new speaker per source rises from 0 to 1/sqrt(n) (same total power for any n). Moving the old
   * panners instead jumped their direction and level in one sample (measured 2026-10-29: the largest sample step at a
   * switch 11-88 times the music's own 99.9th percentile).
   */
  setSources(sources: SourceSpec[]): void {
    const t = this.ctx.currentTime;
    const at = (this.jumpAt = this.jumpPending ?? t + DIP_S);
    for (const sp of this.list) {
      fadeOut(sp.gain.gain, t, at);
      setTimeout(() => { sp.gain.disconnect(); sp.att.disconnect(); sp.panner.disconnect(); }, (at - t) * 1000 + REMOVE_AFTER_MS);
    }
    if (this.port) { fadeOut(this.port.fade.gain, t, at); fadeIn(this.port.fade.gain, 1, at); }
    const g = 1 / Math.sqrt(sources.length);
    this.list = sources.map((s) => {
      const sp = this.add(s, 0);
      fadeIn(sp.gain.gain, g, at);
      return sp;
    });
    this.apply(true);
  }

  /** A room's directivity (rooms.json audio.directivity); missing = DEFAULT_DIRECTIVITY. Every change glides. */
  setDirectivity(d: Directivity | undefined): void {
    this.dir = d ?? DEFAULT_DIRECTIVITY;
    const t = this.ctx.currentTime;
    const at = this.jumpPending;
    const port = this.dir.port;
    if (port && !this.port) {
      const lp = this.ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.Q.value = Q_BUTTERWORTH_DB;
      lp.frequency.value = port.lowpass_hz;
      const fade = this.ctx.createGain();
      fade.gain.value = 0;
      fadeIn(fade.gain, 1, at ?? t);
      const att = this.ctx.createGain();
      att.gain.value = 0;
      let panner: PannerNode | null = null;
      if (this.light) this.inputNode().connect(lp).connect(fade).connect(att).connect(this.out);
      else {
        panner = this.ctx.createPanner();
        panner.panningModel = this.panning;
        panner.rolloffFactor = 0;
        place(panner, port.position, [0, 0, -1], this.ctx);
        this.inputNode().connect(lp).connect(fade).connect(att).connect(panner).connect(this.out);
      }
      this.port = { lp, fade, att, panner, position: port.position };
    } else if (!port && this.port) {
      const old = this.port;
      this.port = null;
      const end = at ?? t + DIP_S;
      fadeOut(old.fade.gain, t, end);
      setTimeout(() => { old.lp.disconnect(); old.fade.disconnect(); old.att.disconnect(); old.panner?.disconnect(); }, (end - t) * 1000 + REMOVE_AFTER_MS);
    } else if (port && this.port) {
      const p = this.port;
      p.position = port.position;
      if (p.panner) place(p.panner, port.position, [0, 0, -1], this.ctx, at ?? t);
      follow(p.lp.frequency, port.lowpass_hz, t);
    }
    if (this.shelf) this.shelf.frequency.setValueAtTime(this.dir.near?.shelf_hz ?? 150, t);
    if (!this.dir.lowpass_hz) {
      // no off-axis low-pass: glide to exactly the Nyquist frequency, where the filter passes everything unchanged
      start(this.lp.frequency, t);
      this.lp.frequency.exponentialRampToValueAtTime(this.nyquist, t + OPEN_S);
    }
    if (this.shelf && !this.dir.near) {
      start(this.shelf.gain, t);
      this.shelf.gain.linearRampToValueAtTime(0, t + OPEN_S);
    }
    this.apply();
  }

  /** The listener moved (engine.updateListener, at most ~30 times a second). */
  listen(position: V3): void {
    this.listener = position;
    this.apply();
  }

  /** Levels and filters for the current listener position. `now`: the new speakers' levels at once (they are silent). */
  private apply(now = false): void {
    const t = this.ctx.currentTime;
    const d = this.dir, L = this.listener;
    for (const s of this.list) {
      const dist = Math.hypot(L[0] - s.src.position[0], L[1] - s.src.position[1], L[2] - s.src.position[2]);
      const v = coneGain(d, offAxisDeg(s.src.position, s.src.forward, L)) * distanceGain(d.ref_m, d.rolloff, dist, d.max_m);
      if (now) { s.att.gain.cancelScheduledValues(t); s.att.gain.setValueAtTime(v, t); } else follow(s.att.gain, v, t);
    }
    if (this.list.length && (d.lowpass_hz || d.near)) {
      const c = centreOf(this.list.map((s) => s.src));
      if (d.lowpass_hz) follow(this.lp.frequency, Math.min(this.nyquist, offAxisLowpass(d, offAxisDeg(c.position, c.forward, L))), t);
      if (d.near && this.shelf) follow(this.shelf.gain, nearShelfDb(d, Math.hypot(L[0] - c.position[0], L[1] - c.position[1], L[2] - c.position[2])), t);
    }
    const port = d.port;
    if (port && this.port) {
      const dist = Math.hypot(L[0] - port.position[0], L[1] - port.position[1], L[2] - port.position[2]);
      follow(this.port.att.gain, port.gain * distanceGain(port.ref_m, port.rolloff, dist, d.max_m), t);
    }
  }

  /** Where the shared filters start (the port takes the music before them). */
  private inputNode(): AudioNode {
    return this.shelf ?? this.lp;
  }
}

function place(p: PannerNode, pos: V3, fwd: V3, ctx: BaseAudioContext, at = ctx.currentTime): void {
  p.positionX.setValueAtTime(pos[0], at); p.positionY.setValueAtTime(pos[1], at); p.positionZ.setValueAtTime(pos[2], at);
  p.orientationX.setValueAtTime(fwd[0], at); p.orientationY.setValueAtTime(fwd[1], at); p.orientationZ.setValueAtTime(fwd[2], at);
}

/** End any automation where it is now (Firefox lacks cancelAndHoldAtTime and prunes past events itself). */
export function hold(p: AudioParam, t: number): void {
  const h = (p as AudioParam & { cancelAndHoldAtTime?: (t: number) => AudioParam }).cancelAndHoldAtTime;
  if (h) h.call(p, t);
  else {
    // Firefox: after cancelScheduledValues a following setTargetAtTime starts from the param's default value (1 for
    // a gain), not from a value set with `.value`. The overlay's brown noise then started at gain 0.9 instead of 0
    // (a loud burst arriving in the hallway, Firefox 158, 2026-10-30). Pin the current value.
    const v = p.value;
    p.cancelScheduledValues(t);
    p.setValueAtTime(v, t);
  }
}

/** Glide to v with one pending automation event. */
function follow(p: AudioParam, v: number, t: number): void {
  hold(p, t);
  p.setTargetAtTime(v, t, FOLLOW_S);
}

/**
 * Hold p where it is and set an event at t for a ramp to start from. Without one a ramp starts at the previous
 * event (time 0 when there is none: a linear fall to 0 then was nearly over at once), and after cancelAndHoldAtTime
 * over a setTargetAtTime Chrome started an exponential ramp near 0 (the low-pass closed: silence for 80 ms).
 */
function start(p: AudioParam, t: number): void {
  hold(p, t);
  p.setValueAtTime(p.value, t);
}

/** A linear fall from where p is at t to 0 at `end`. */
function fadeOut(p: AudioParam, t: number, end: number): void {
  start(p, t);
  p.linearRampToValueAtTime(0, Math.max(end, t + 0.005));
}

/** A rise from 0 at `at` toward v. */
function fadeIn(p: AudioParam, v: number, at: number): void {
  p.setValueAtTime(0, at);
  p.setTargetAtTime(v, at, RISE_S);
}
